import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext.jsx';
import {
  getConversationGrants,
  putConversationGrants,
  postConversationEmails,
} from '../../services/api.js';
import '../Dashboard/AddToDashboardModal.css';
import './ShareModal.css';

/**
 * Owner share picker: company users, replace grants, then copy link or email.
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {function} props.onClose
 * @param {string} props.sessionId
 */
const ShareModal = ({ isOpen, onClose, sessionId }) => {
  const { t, i18n } = useTranslation();
  const { activeCompany } = useAuth();
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState({});
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailFailed, setEmailFailed] = useState([]);
  const [savedGrantCount, setSavedGrantCount] = useState(0);
  const [shareMode, setShareMode] = useState('email');

  const busy = saving || sendingEmail;

  useEffect(() => {
    if (!isOpen || !sessionId) return undefined;
    let cancelled = false;
    setLink('');
    setCopied(false);
    setError(null);
    setEmailMessage('');
    setEmailSent(false);
    setEmailFailed([]);
    setSavedGrantCount(0);
    setShareMode('email');
    setLoading(true);
    (async () => {
      try {
        const data = await getConversationGrants(sessionId);
        if (cancelled) return;
        const granted = new Set((data.granted_user_ids || []).map(String));
        const next = {};
        (data.members || []).forEach((m) => {
          next[m.user_id] = granted.has(String(m.user_id));
        });
        setMembers(data.members || []);
        setSelected(next);
      } catch (err) {
        if (!cancelled) setError(err.message || t('chat.shareLoadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, sessionId, t]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !busy) onClose();
  };

  const toggleUser = (userId) => {
    setSelected((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const buildLink = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('session_id', sessionId);
    if (activeCompany?.id != null) {
      url.searchParams.set('company', String(activeCompany.id));
    }
    return url.toString();
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setCopied(false);
    try {
      const userIds = Object.keys(selected).filter((id) => selected[id]);
      await putConversationGrants(sessionId, userIds);
      setSavedGrantCount(userIds.length);
      setLink(buildLink());
      setEmailMessage(t('chat.shareEmailDefaultMessage'));
      setEmailSent(false);
      setEmailFailed([]);
      setShareMode('email');
    } catch (err) {
      setError(err.message || t('chat.shareSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setError(null);
      setCopied(true);
    } catch {
      setError(t('chat.shareCopyError'));
    }
  };

  const handleSendEmail = async () => {
    if (emailSent || sendingEmail || savedGrantCount === 0 || !emailMessage.trim()) {
      return;
    }
    setSendingEmail(true);
    setError(null);
    try {
      const language = i18n.language && i18n.language.startsWith('no') ? 'no' : 'en';
      const data = await postConversationEmails(sessionId, {
        message: emailMessage.trim(),
        conversation_url: link,
        language,
      });
      setEmailSent(true);
      const failed = (data.results || [])
        .filter((row) => !row.ok)
        .map((row) => row.recipient);
      setEmailFailed(failed);
    } catch (err) {
      setError(err.message || t('chat.shareEmailSendError'));
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>{t('chat.shareTitle')}</h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label={t('common.close')}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {loading && (
            <div className="modal-loading">
              <div className="loading-spinner"></div>
              <p>{t('app.loading')}</p>
            </div>
          )}
          {error && <div className="error-message">{error}</div>}
          {!loading && !link && !error && members.length === 0 && (
            <p className="share-empty">{t('chat.shareNoUsers')}</p>
          )}
          {!loading && !link && members.length > 0 && (
            <ul className="share-user-list">
              {members.map((member) => (
                <li key={member.user_id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(selected[member.user_id])}
                      disabled={saving}
                      onChange={() => toggleUser(member.user_id)}
                    />
                    {member.email}
                  </label>
                </li>
              ))}
            </ul>
          )}
          {link && (
            <div className="share-options">
              <div className="share-mode-tabs" role="tablist" aria-label={t('chat.shareModeLabel')}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={shareMode === 'email'}
                  className={`share-mode-tab${shareMode === 'email' ? ' active' : ''}`}
                  onClick={() => setShareMode('email')}
                >
                  {t('chat.shareOptionEmail')}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={shareMode === 'link'}
                  className={`share-mode-tab${shareMode === 'link' ? ' active' : ''}`}
                  onClick={() => setShareMode('link')}
                >
                  {t('chat.shareOptionLink')}
                </button>
              </div>
              {shareMode === 'email' && (
                <section className="share-option">
                  <textarea
                    className="share-email-message"
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    disabled={sendingEmail || emailSent}
                    rows={4}
                  />
                  {emailSent && emailFailed.length === 0 && (
                    <p className="share-email-status">{t('chat.shareEmailSent')}</p>
                  )}
                  {emailSent && emailFailed.length > 0 && (
                    <p className="share-email-status">
                      {t('chat.shareEmailPartialFail', { emails: emailFailed.join(', ') })}
                    </p>
                  )}
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleSendEmail}
                    disabled={
                      sendingEmail
                      || emailSent
                      || savedGrantCount === 0
                      || !emailMessage.trim()
                    }
                  >
                    {sendingEmail ? t('chat.shareEmailSending') : t('chat.shareEmailSend')}
                  </button>
                </section>
              )}
              {shareMode === 'link' && (
                <section className="share-option">
                  <input type="text" readOnly value={link} className="share-link-input" />
                  <button
                    type="button"
                    className={`btn-primary${copied ? ' is-copied' : ''}`}
                    onClick={handleCopy}
                  >
                    {copied ? t('chat.shareCopied') : t('chat.shareCopy')}
                  </button>
                </section>
              )}
            </div>
          )}
        </div>

        {!link && (
          <div className="modal-footer">
            <button
              type="button"
              className="btn-primary"
              onClick={handleSave}
              disabled={loading || saving || Boolean(error && members.length === 0)}
            >
              {saving ? t('chat.shareSaving') : t('chat.shareSave')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareModal;
